/**
 * useVoiceRecorder — enregistrement d'une note vocale via MediaRecorder.
 * Choisit audio/webm (Chrome/Android) avec repli audio/mp4 (Safari/iPad). Expose la durée
 * en direct, une limite (défaut 5 min), et renvoie le Blob + le nom de fichier à l'arrêt.
 * La permission micro est demandée au premier démarrage ; erreur claire si refusée.
 */
import { useState, useRef, useCallback, useEffect } from 'react';

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return '';
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) || '';
}

export function useVoiceRecorder({ maxSeconds = 300 } = {}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const resolveRef = useRef(null);

  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError("L'enregistrement audio n'est pas disponible sur cet appareil.");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = rec.mimeType || 'audio/webm';
        const ext = type.includes('mp4') || type.includes('aac') ? 'mp4' : 'webm';
        const blob = new Blob(chunksRef.current, { type });
        cleanup();
        setRecording(false);
        if (resolveRef.current) { resolveRef.current({ blob, filename: `note.${ext}`, duration: seconds }); resolveRef.current = null; }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= maxSeconds) { rec.state === 'recording' && rec.stop(); }
        return s + 1;
      }), 1000);
      return true;
    } catch (e) {
      setError(e?.name === 'NotAllowedError' ? 'Accès au micro refusé. Autorisez le microphone.' : "Micro indisponible.");
      cleanup();
      return false;
    }
  }, [cleanup, maxSeconds, seconds]);

  const stop = useCallback(() => new Promise((resolve) => {
    if (recRef.current && recRef.current.state === 'recording') {
      resolveRef.current = resolve;
      recRef.current.stop();
    } else { resolve(null); }
  }), []);

  return { recording, seconds, error, start, stop };
}
